<template>
    <aside class="eureka-sidebar pretty-scroll relative" :class="{ collapsed: isCollapsed }">
        <!-- Logo 区域 -->
        <a href="/" class="sidebar-logo">
            <img
                v-if="!isCollapsed"
                src="@/assets/nav-bar/logo.svg"
                alt="Eureka"
                class="logo-full"
            >
            <img
                v-else
                src="@/assets/nav-bar/mini-logo.svg"
                alt="Eureka"
                class="logo-mini"
            >
        </a>

        <div class="sidebar-toggle" @click="toggleSidebar">
            <PanelLeftClose v-if="!isCollapsed" :size="18" :stroke-width="2" />
            <PanelLeftOpen v-else :size="18" :stroke-width="2" />
            <span class="toggle-text">{{ isCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar') }}</span>
        </div>

        <div class="sidebar-menu">
            <div class="menu-item" :class="{ active: isActive('/') }" @click="navigateTo('/')">
                <Home :size="18" :stroke-width="2" />
                <span class="menu-text">{{ t('nav.home') }}</span>
            </div>
            <div class="menu-group-title">
                {{ t('nav.workspaces') }}
            </div>
            <div class="menu-item" :class="{ active: isActive('/patent-drafting/projects') }" @click="navigateTo('/patent-drafting/projects')">
                <FileEdit :size="18" :stroke-width="2" />
                <span class="menu-text">{{ t('nav.draftingCenter') }}</span>
            </div>
            <div class="menu-item" :class="{ active: isActive('/templates') }" @click="navigateTo('/templates')">
                <LayoutTemplate :size="18" :stroke-width="2" />
                <span class="menu-text">{{ t('nav.templates') }}</span>
            </div>
            <div class="menu-item" :class="{ active: isActive('/instructions') }" @click="navigateTo('/instructions')">
                <Lightbulb :size="18" :stroke-width="2" />
                <span class="menu-text">{{ t('nav.instructions') }}</span>
            </div>
            <!-- Diagram 菜单项已临时屏蔽 -->
            <!-- <div class="menu-item" :class="{ active: isActive('/diagram') }" @click="navigateTo('/diagram')">
                <LineChart :size="18" :stroke-width="2" />
                <span class="menu-text">{{ t('nav.diagram') }}</span>
            </div> -->
            <div v-if="DISPLAY_FEATURES_DEVELOPING" class="menu-item" :class="{ active: isActive('/writing-styles') }" @click="navigateTo('/writing-styles')">
                <FileEdit :size="18" :stroke-width="2" />
                <span class="menu-text">{{ t('nav.styles') }}</span>
            </div>
            <!-- <div class="menu-group-title">
                {{ t('nav.settings') }}
            </div> -->
            <!-- Pricing 菜单项已临时屏蔽 -->
            <!-- <div class="menu-item" :class="{ active: isActive('/upgrade') }" @click="navigateTo('/upgrade')">
                <CreditCard :size="18" :stroke-width="2" />
                <span class="menu-text">{{ t('nav.pricing') }}</span>
            </div> -->
        </div>

        <!-- 用户中心/登录按钮 -->
        <div class="sidebar-footer">
            <PtAvatarPC
                v-if="headerService"
                :service="headerService"
                open
                @clickLogin="handleLogin"
            />
        </div>
    </aside>
</template>

<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { PanelLeftClose, PanelLeftOpen, Home, FileEdit, LayoutTemplate, Lightbulb, CreditCard } from 'lucide-vue-next';
import { PtAvatarPC } from '@patsnap-biz/nav-user-avatar';
import { headerService } from '@/services/header';
import { useNavBar } from '@/composables/useNavBar';
import { useLoginSdk } from '@/composables/useLoginSdk';
import { DISPLAY_FEATURES_DEVELOPING } from '@/constants/common';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// 使用统一的NavBar状态管理
const { isCollapsed, toggleSidebar } = useNavBar();

// 初始化登录 SDK
const { openLoginDialog } = useLoginSdk();

const navigateTo = (path: string) => {
    router.push(path);
};

const isActive = (path: string) => {
    return route.path === path;
};

const handleLogin = () => {
    openLoginDialog();
};
</script>

<style scoped lang="scss">
.eureka-sidebar {
    height: 100vh;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 220px;
    color: #fff;
    background-color: #0456d1;
    border-right: none;
    transition: width 0.3s ease;

    &.collapsed {
        width: 56px;

        .menu-text,
        .toggle-text {
            opacity: 0;
            visibility: hidden;
            width: 0;
            overflow: hidden;
        }

        .menu-group-title {
            display: none;
        }

        .menu-item {
            justify-content: center;
            padding: 0;
            gap: 0;
        }

        .sidebar-toggle {
            justify-content: center;
            padding: 0;
            gap: 0;
        }

        .sidebar-logo {
            justify-content: center;
            padding: 4px;
        }

        .sidebar-footer {
            padding: 16px 0px 8px 0px;
            :deep(.patsnap-biz-nav-user_center > ul) {
                flex-direction: column !important;
                align-items: center !important;
                gap: 4px !important;
                height: auto !important;
            }
            :deep(.patsnap-biz-nav-user_center--logged) {
                margin-left: 0 !important;
            }
        }
    }
}

.sidebar-logo {
    display: flex;
    align-items: center;
    height: 60px;
    padding: 4px;
    padding-left: 14px;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;

    &::after {
        content: '';
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 0;
        height: 1px;
        background-color: rgb(255 255 255 / 15%);
    }

    .logo-full {
        width: 100px;
        height: 30px;
        object-fit: contain;
    }

    .logo-mini {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }

    &:hover {
        opacity: 0.9;
    }
}

.sidebar-toggle {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 40px;
    padding: 0 16px;
    color: rgb(255 255 255 / 70%);
    cursor: pointer;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 0;
        height: 1px;
        background-color: rgb(255 255 255 / 15%);
    }

    svg {
        flex-shrink: 0;
    }

    .toggle-text {
        font-size: 14px;
        white-space: nowrap;
        transition: opacity 0.2s ease 0.15s, visibility 0.2s ease 0.15s;
    }

    &:hover {
        color: #fff;
    }
}

.sidebar-menu {
    flex: 1;
    padding: 8px 0;
    overflow-y: auto;
    min-height: 0; // 允许缩小以给 footer 留空间
}

.menu-item {
    display: flex;
    gap: 12px;
    align-items: center;
    height: 40px;
    padding: 0 16px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
    overflow: hidden;

    .menu-text {
        white-space: nowrap;
        transition: opacity 0.2s ease 0.15s, visibility 0.2s ease 0.15s;
    }

    &:hover {
        color: #fff;
        background-color: rgb(255 255 255 / 10%);
    }

    &.active {
        color: #fff;
        background-color: rgb(255 255 255 / 15%);
        border-right: 3px solid #fff;
    }

    svg {
        flex-shrink: 0;
        color: inherit;
    }

    &.active svg {
        color: #fff;
    }
}

.menu-divider {
    height: 1px;
    margin: 8px 16px;
    background-color: rgb(255 255 255 / 15%);
}

.menu-group-title {
    padding: 8px 16px;
    margin-top: 8px;
    font-size: 11px;
    font-weight: 700;
    color: rgb(255 255 255 / 50%);
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    transition: opacity 0.2s ease 0.15s, visibility 0.2s ease 0.15s;
}

.sidebar-footer {
    padding: 4px 16px;
    border-top: 1px solid rgb(255 255 255 / 15%);
    flex-shrink: 0; // 防止被压缩
}

.login-button {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 16px;
    color: #fff;
    background: transparent;
    border: 1px solid rgb(255 255 255 / 30%);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;

    svg {
        flex-shrink: 0;
    }

    &:hover {
        background: rgb(255 255 255 / 10%);
        border-color: rgb(255 255 255 / 50%);
    }

    .login-text {
        font-size: 14px;
    }
}

.eureka-sidebar.collapsed .login-button {
    padding: 8px;
    justify-content: center;
}
</style>
